import { useEffect, useMemo, useRef, useState } from 'react';
import type { CampingSpot, Coordinates } from '../types';
import { getDistance } from '../services/geometryUtils';

const IDLE_MS = 60 * 1000;            // 60s without user interaction
const SPEED_THRESHOLD_MPS = 5;        // ~18 km/h — covers driving, fast cycling
const SPEED_EXIT_GRACE_MS = 30 * 1000; // exit if speed stays below threshold this long
const EVAL_INTERVAL_MS = 2000;        // re-check trigger conditions

interface AutoNavOptions {
  enabled: boolean;
  userPosition: Coordinates | null;
  // m/s, e.g. from active GPS recording. If null/undefined, we estimate
  // from userPosition deltas so auto-nav also works when not recording.
  currentSpeed: number | null;
  spots: CampingSpot[];
}

interface AutoNavResult {
  active: boolean;
  nearestSpot: CampingSpot | null;
  distanceM: number | null;
  exit: () => void;
}

// Returns true if event target is inside the map area (Leaflet container).
// Map pan/zoom should NOT count as "user interacting with the app UI".
function isMapTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return !!target.closest('.leaflet-container');
}

export function useAutoNavigationMode({
  enabled,
  userPosition,
  currentSpeed,
  spots,
}: AutoNavOptions): AutoNavResult {
  const [active, setActive] = useState(false);
  const lastInteractionRef = useRef<number>(Date.now());
  const slowSinceRef = useRef<number | null>(null);
  const manuallyExitedRef = useRef<boolean>(false);
  const lastFixRef = useRef<{ pos: Coordinates; t: number } | null>(null);
  const estimatedSpeedRef = useRef<number>(0);

  // Estimate speed from position deltas when explicit speed is unavailable.
  // Smoothed slightly to ride out single-fix jitter.
  useEffect(() => {
    if (!userPosition) return;
    const now = Date.now();
    const prev = lastFixRef.current;
    if (prev) {
      const dt = (now - prev.t) / 1000;
      if (dt > 0.5 && dt < 120) {
        const dist = getDistance(prev.pos.latitude, prev.pos.longitude, userPosition.latitude, userPosition.longitude);
        const instant = dist / dt;
        // 30/70 EMA — favors recent reading but absorbs noise
        estimatedSpeedRef.current = 0.7 * instant + 0.3 * estimatedSpeedRef.current;
      }
    }
    lastFixRef.current = { pos: userPosition, t: now };
  }, [userPosition]);

  // Track non-map user interactions
  useEffect(() => {
    if (!enabled) return;
    const onActivity = (e: Event) => {
      if (isMapTarget(e.target)) return;
      lastInteractionRef.current = Date.now();
      // A real interaction means user is using the app — exit auto-nav if active
      if (active) {
        manuallyExitedRef.current = true;
        setActive(false);
      }
    };
    const opts = { capture: true, passive: true } as AddEventListenerOptions;
    document.addEventListener('pointerdown', onActivity, opts);
    document.addEventListener('keydown', onActivity, opts);
    document.addEventListener('wheel', onActivity, opts);
    return () => {
      document.removeEventListener('pointerdown', onActivity, opts);
      document.removeEventListener('keydown', onActivity, opts);
      document.removeEventListener('wheel', onActivity, opts);
    };
  }, [enabled, active]);

  // Reset manual-exit flag on visibility change so user can re-enter naturally
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible') {
        lastInteractionRef.current = Date.now();
        manuallyExitedRef.current = false;
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  // Periodic evaluator: idle + moving fast + foreground → activate
  useEffect(() => {
    if (!enabled) {
      setActive(false);
      return;
    }
    const tick = () => {
      const now = Date.now();
      const isVisible = document.visibilityState === 'visible';
      const speed = currentSpeed ?? estimatedSpeedRef.current;
      const isMovingFast = speed >= SPEED_THRESHOLD_MPS;
      const isIdle = now - lastInteractionRef.current >= IDLE_MS;

      if (isMovingFast) {
        slowSinceRef.current = null;
      } else if (slowSinceRef.current === null) {
        slowSinceRef.current = now;
      }

      if (active) {
        // Exit if speed stayed low long enough, or app went to background
        if (!isVisible) return; // pause but don't exit
        if (slowSinceRef.current !== null && now - slowSinceRef.current >= SPEED_EXIT_GRACE_MS) {
          setActive(false);
          slowSinceRef.current = null;
        }
      } else {
        // Manuell exit (bruker rørte appen mens aktiv, eller trykket X) undertrykker
        // re-aktivering — men KUN til brukeren har vært inaktiv igjen (isIdle). Ellers
        // ble flippen sittende hele økta (nullstilles ellers bare ved fane-synlighet),
        // så auto-nav aldri startet på nytt etter at man brukte appen litt.
        if (manuallyExitedRef.current) {
          if (!isIdle) return;                 // fortsatt i nåde-vinduet etter interaksjon
          manuallyExitedRef.current = false;   // inaktiv igjen → la auto-nav re-armere
        }
        if (isVisible && isMovingFast && isIdle) {
          setActive(true);
        }
      }
    };
    const id = setInterval(tick, EVAL_INTERVAL_MS);
    tick();
    return () => clearInterval(id);
  }, [enabled, active, currentSpeed]);

  // Compute nearest spot only while active to avoid wasted work
  const { nearestSpot, distanceM } = useMemo(() => {
    if (!active || !userPosition || spots.length === 0) {
      return { nearestSpot: null, distanceM: null };
    }
    let best: CampingSpot | null = null;
    let bestDist = Infinity;
    for (const s of spots) {
      if (!s.location) continue;
      const d = getDistance(
        userPosition.latitude,
        userPosition.longitude,
        s.location.latitude,
        s.location.longitude,
      );
      if (d < bestDist) {
        bestDist = d;
        best = s;
      }
    }
    return { nearestSpot: best, distanceM: best ? bestDist : null };
  }, [active, userPosition, spots]);

  const exit = () => {
    manuallyExitedRef.current = true;
    // Behandle exit som en interaksjon slik at re-armeringen får samme 60s nåde-vindu.
    // Uten dette ville en eksplisitt exit (X-knapp) sprette rett tilbake, siden isIdle
    // alt er sant mens auto-nav er aktiv.
    lastInteractionRef.current = Date.now();
    setActive(false);
  };

  return { active, nearestSpot, distanceM, exit };
}
